import React, { useState } from 'react'
import styled from '@emotion/styled'

import { PAGE_BREAKPOINTS } from 'parameters/index'

import { RangeOptions } from '../Utils/types'
import { InputRange } from '../Components/InputRange'
import { ResultsSection } from './components/ResultsSection'
import { SectionTitle } from './components/SectionTitle'
import { buildInitialState, CALCULATOR_INFORMATION_TOOLTIP_TEXT, debtCalcInputConfig, InputState } from './config'
import { ClientOnlyRender } from 'lib/renderWrappers/ClientOnly'
import { Stack } from 'components/layout/primitives'
import { Button } from 'ui/inputs/Button/LegacyButtonCompatibility'

interface Props {
	isExpanded?: boolean
	showTitle?: boolean
}

const DebtConsolidationCalculator: React.FC<React.PropsWithChildren<Props>> = ({
	isExpanded = false,
	showTitle = true
}) => {
	const [inputState, setInputState] = useState<InputState>(buildInitialState())
	const [expanded, setExpanded] = useState(isExpanded)

	const buildChangeHandler = (key: string, value: number) => {
		setInputState(prev => ({
			...prev,
			[key]: value
		}))
		if (!expanded) setExpanded(true)
	}

	return (
		<CalculatorWrapper>
			{showTitle && (
				<TitleWrapper>
					<Title>Debt Consolidation Calculator</Title>
				</TitleWrapper>
			)}
			<Section>
				<Stack>
					<SectionTitle sectionNumber={1} title="Debt Information" tooltipText={CALCULATOR_INFORMATION_TOOLTIP_TEXT} />
					<SectionItem>
						<Range>
							{Object.entries(debtCalcInputConfig).map(([key, value]: [string, RangeOptions<InputState>]) => {
								return (
									<InputRange<InputState>
										key={key}
										name={key}
										options={value}
										userInput={inputState}
										onChange={buildChangeHandler}
									/>
								)
							})}
						</Range>
					</SectionItem>
				</Stack>

				{expanded ? (
					<ClientOnlyRender>
						<ResultsSection inputState={inputState} />
					</ClientOnlyRender>
				) : (
					<ButtonWrapper>
						<Button onClick={(): void => setExpanded(true)}>Calculate</Button>
					</ButtonWrapper>
				)}
			</Section>
		</CalculatorWrapper>
	)
}

export default DebtConsolidationCalculator

export const CalculatorWrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 24px 16px;
	border-radius: 8px;
`

const TitleWrapper = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 24px;
`

const Title = styled.h2`
	font-size: 24px;
	margin-right: 16px;
`

const Section = styled.div`
	display: flex;
	flex-direction: column;
`

const SectionItem = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	@media screen and (${PAGE_BREAKPOINTS.MOBILE}) {
		flex-direction: column;
	}
`

const Range = styled.div`
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 100%;
`

const ButtonWrapper = styled.div`
	max-width: 277px;
	display: flex;
	button {
		width: 100%;
	}
	@media screen and (${PAGE_BREAKPOINTS.MOBILE}) {
		max-width: 100%;
	}
`
